import { Alert, Button, CircularProgress } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { SLOT_KINDS } from "../referralOnboardingApi";
import { MobileStepper, StepRail } from "./WizardParts";
import WizardStepFields from "./WizardStepFields";
import { DOC_MAX_BYTES, INITIAL_FORM, stepsFor, validateStep } from "./wizardConfig";

const errorText = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const isAcceptedFile = (file) =>
  file.type.startsWith("image/") ||
  file.type === "application/pdf" ||
  /\.(pdf|jpe?g|png|webp)$/i.test(file.name);

/**
 * The referral partner onboarding wizard, shared by an invited partner (token, sets a password) and a
 * signed-in staff member (session, no password). The `adapter` supplies the data calls and what is
 * already known: fields named in `lockedFields` are shown but cannot be edited, and document slots in
 * `documentsOnFile` count as provided.
 *
 * adapter: { load, upload, complete, requirePassword, lockedHint, preview?, describeError? }
 * describeError(err) -> { message, detail?, terminal? }; a terminal failure replaces the form.
 */
const OnboardingWizard = ({ adapter, copy, renderDone, onDone, asideFooter }) => {
  const steps = useMemo(() => stepsFor({ requirePassword: adapter.requirePassword }), [adapter]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState(null);
  const [known, setKnown] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [uploads, setUploads] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adapter
      .load()
      .then((data) => {
        if (cancelled) return;
        setKnown(data);
        setForm({ ...INITIAL_FORM, ...data.form });
      })
      .catch((err) => {
        if (!cancelled) setFailure(adapter.describeError?.(err) || { message: errorText(err, "Could not load your details.") });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const lockedFields = useMemo(() => new Set(known?.lockedFields || []), [known]);
  const docsOnFile = useMemo(() => new Set(known?.documentsOnFile || []), [known]);
  const isLocked = (name) => lockedFields.has(name);
  const lockedHint = (name) => (isLocked(name) ? adapter.lockedHint : null);
  const documentOnFile = (key) => docsOnFile.has(key);

  const orgLabel = known?.organisationName || "Kothar";
  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  const clearError = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const patch = (event) => {
    const { name, value, type, checked } = event.target;
    if (isLocked(name)) return;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    clearError(name);
  };

  const patchField = (name, value) => {
    if (isLocked(name)) return;
    setForm((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const submit = useMutation(() => adapter.complete(form, known?.termsVersion), {
    onSuccess: (result) => {
      setSubmitted(true);
      onDone?.(result);
    },
    onError: (err) => {
      const described = adapter.describeError?.(err);
      if (described?.terminal) {
        setFailure(described);
        return;
      }
      toast.error(errorText(err, "Could not submit your profile"));
    },
  });

  const selectDocument = (key, file) => {
    if (!file) {
      patchField(key, null);
      setUploads((prev) => ({ ...prev, [key]: { status: "idle" } }));
      return;
    }
    if (file.size > DOC_MAX_BYTES) {
      toast.error(`${file.name} is too large. Maximum size is 10 MB.`);
      return;
    }
    if (!isAcceptedFile(file)) {
      toast.error("Use a PDF or image file (JPG, PNG, WebP).");
      return;
    }
    patchField(key, file);
    if (adapter.preview) return;
    // Upload straight away so a failed upload surfaces before the last step.
    setUploads((prev) => ({ ...prev, [key]: { status: "uploading" } }));
    adapter
      .upload(SLOT_KINDS[key], file)
      .then(() => setUploads((prev) => ({ ...prev, [key]: { status: "done" } })))
      .catch((err) => {
        const described = adapter.describeError?.(err);
        if (described?.terminal) {
          setFailure(described);
          return;
        }
        setUploads((prev) => ({
          ...prev,
          [key]: { status: "error", error: errorText(err, "Upload failed. Try again.") },
        }));
      });
  };

  const goNext = () => {
    const found = validateStep(step.id, form, {
      requirePassword: adapter.requirePassword,
      isLocked,
      onFile: documentOnFile,
    });
    setErrors(found);
    if (Object.keys(found).length > 0) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    if (isLast) {
      submit.mutate();
      return;
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };

  const goBack = () => {
    setErrors({});
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const shell = (children) => (
    <div className="min-h-screen bg-slate-100 lg:bg-transparent lg:grid lg:grid-cols-[minmax(280px,38%)_1fr]">
      <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-slate-900 text-white px-8 py-10">
        <div
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(ellipse at 20% 10%, rgba(56,189,248,0.35), transparent 55%), radial-gradient(ellipse at 90% 80%, rgba(16,185,129,0.25), transparent 50%)",
          }}
        />
        <div className="relative z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-200/90 m-0 mb-3">
            Referral partner
          </p>
          <h1 className="text-3xl font-bold leading-tight m-0 mb-3">{copy.headline(orgLabel)}</h1>
          <p className="text-sm text-white/70 m-0 leading-relaxed max-w-sm">{copy.intro}</p>
          <div className="mt-10">
            <StepRail
              steps={steps}
              stepIndex={stepIndex}
              onJump={(index) => {
                if (index <= stepIndex) {
                  setErrors({});
                  setStepIndex(index);
                }
              }}
            />
          </div>
        </div>
        <div className="relative z-10">
          <p className="text-xs text-white/45 m-0">
            Step {stepIndex + 1} of {steps.length}
          </p>
          {asideFooter}
        </div>
      </aside>

      <main className="min-h-screen flex items-stretch lg:items-center justify-center px-4 py-6 sm:py-10">
        <div className="w-full max-w-xl">
          {children}
          {asideFooter ? <div className="lg:hidden mt-4 text-center">{asideFooter}</div> : null}
        </div>
      </main>
    </div>
  );

  if (loading) {
    return shell(
      <div className="flex justify-center py-24">
        <CircularProgress />
      </div>
    );
  }

  if (failure) {
    return shell(
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-red-500 text-xl font-bold">!</span>
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2">{copy.failureTitle}</h2>
        <p className="text-slate-500 m-0">{failure.message}</p>
        {failure.detail ? <p className="text-slate-500 mt-3 mb-0">{failure.detail}</p> : null}
      </div>
    );
  }

  if (submitted && renderDone) {
    return shell(renderDone());
  }

  return shell(
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-5 sm:px-7 pt-6 sm:pt-7 pb-4 border-b border-slate-100">
        <MobileStepper steps={steps} stepIndex={stepIndex} />
        <p className="hidden lg:block text-xs font-semibold uppercase tracking-wide text-sky-700 m-0 mb-1">
          Step {stepIndex + 1} · {step.short}
        </p>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 m-0">{step.title}</h2>
        <p className="text-sm text-slate-500 mt-1.5 mb-0">{step.hint}</p>
        {copy.notice ? (
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            {copy.notice}
          </Alert>
        ) : null}
      </div>

      <div key={step.id} className="px-5 sm:px-7 py-6 space-y-4 animate-[fadeSlide_220ms_ease-out]">
        <style>{`
          @keyframes fadeSlide {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <WizardStepFields
          stepId={step.id}
          form={form}
          errors={errors}
          patch={patch}
          patchField={patchField}
          lockedHint={lockedHint}
          requirePassword={adapter.requirePassword}
          documentOnFile={documentOnFile}
          uploads={uploads}
          selectDocument={selectDocument}
          terms={{ version: known?.termsVersion, html: known?.termsHtml }}
          orgLabel={orgLabel}
        />
      </div>

      <div className="px-5 sm:px-7 py-4 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between gap-3">
        <Button
          variant="text"
          onClick={goBack}
          disabled={stepIndex === 0 || submit.isLoading}
          sx={{ color: "text.secondary" }}
        >
          Back
        </Button>
        <Button variant="contained" onClick={goNext} disabled={submit.isLoading} size="large">
          {submit.isLoading ? "Submitting…" : isLast ? copy.submitLabel : "Continue"}
        </Button>
      </div>
    </div>
  );
};

export default OnboardingWizard;
